import React, {
  forwardRef,
  useMemo,
  useRef,
  CSSProperties,
  HTMLAttributes,
  PointerEvent,
  PointerEventHandler,
} from 'react';
import classnames from 'classnames';
import { Transition } from 'framer-motion';
import { v1 } from 'uuid';
import { getScale, getScaleBandWidth, getScaleInvert, mergeRefs, useResizeObserver } from './utils';
import VizContext, { Props as VizContextProps } from './VizContext';
import { SeriesPointXY, ScaleString } from './Types';

export interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'onPointerMove'> {
  /** The height of the visualisation. This includes any height needed for the axis components. */
  height: number;
  /**
   * Event handler for when a pointer is moved over the artboard area of the visualisation.
   *
   * @param event The original React PointerEvent.
   * @param point The relative Bitviz SeriesPointXY point that the pointer is over.
   */
  onPointerMove?: (event: PointerEvent, point: SeriesPointXY) => void;
  /**
   * Space given at the bottom artboard area, use for allowing space for a given axis
   *
   * @default 0
   */
  paddingBottom?: number;
  /**
   * Space given at the left artboard area, use for allowing space for a given axis
   *
   * @default 0
   */
  paddingLeft?: number;
  /**
   * Space given at the right artboard area, use for allowing space for a given axis
   *
   * @default 0
   */
  paddingRight?: number;
  /**
   * Space given at the top artboard area, use for allowing space for a given axis
   *
   * @default 0
   */
  paddingTop?: number;
  /**
   * Transition to be used on all viz animations. See Framer Motion's Transition object.
   */
  transition?: Transition;
  /**
   * Optional width of the visualisation. This includes any width needed for the axis components.
   * If omitted, the full width of the container will be used.
   **/
  width?: number;
  /** The domain values to be used across the X axis */
  xDomain: number[];
  /** The inner padding between series points across the X domain  */
  xDomainPaddingInner?: number;
  /** The outer padding around the series for the X domain */
  xDomainPaddingOuter?: number;
  /** The type of scaling for the X axis domain */
  xDomainScale?: ScaleString;
  /** The lower and upper bounds of the y series domain. */
  yDomain: number[];
  /** The inner padding between series points across the Y domain  */
  yDomainPaddingInner?: number;
  /** The outer padding around the series for the Y domain */
  yDomainPaddingOuter?: number;
  /** The type of scaling for the Y axis domain */
  yDomainScale?: ScaleString;
}

const styleViz: CSSProperties = {
  display: 'flex',
  flex: '1 1 0%',
};

const styleVizContainer: CSSProperties = {
  display: 'flex',
  position: 'relative',
  flex: '1 1 0%',
};

/**
 * The Viz component is the top level container for all of the other components.
 * It acts as the bounds calculator and communicates the relative positions for
 * series through the ContextAPI.
 */
const Viz = forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
  const {
    children,
    className,
    height,
    onPointerMove,
    onPointerLeave,
    paddingBottom = 0,
    paddingLeft = 0,
    paddingRight = 0,
    paddingTop = 0,
    style,
    transition,
    width,
    xDomain,
    xDomainPaddingInner = 0,
    xDomainPaddingOuter = 0,
    xDomainScale = 'linear',
    yDomain,
    yDomainPaddingInner = 0,
    yDomainPaddingOuter = 0,
    yDomainScale = 'linear',
    ...rest
  } = props;

  const refContainer = useRef<HTMLDivElement>(null);
  const [size, refSize] = useResizeObserver();
  const refMerged = mergeRefs([refContainer, refSize]);
  const refArtboardId = useRef(v1());

  const context: VizContextProps = useMemo(() => {
    const artboardHeight = size.height - paddingBottom - paddingTop;
    const artboardWidth = size.width - paddingRight - paddingLeft;

    const artboardTop = paddingTop;
    const artboardRight = size.width - paddingRight;
    const artboardBottom = size.height - paddingBottom;
    const artboardLeft = paddingLeft;

    const xScale = getScale(xDomainScale, {
      domain: xDomain,
      paddingInner: xDomainPaddingInner,
      paddingOuter: xDomainPaddingOuter,
      range: [artboardLeft, artboardRight],
    });

    const yScale = getScale(yDomainScale, {
      domain: yDomain,
      paddingInner: yDomainPaddingInner,
      paddingOuter: yDomainPaddingOuter,
      range: [artboardBottom, artboardTop],
    });

    const xScaleOffset = getScaleBandWidth(xScale) / 2;
    const yScaleOffset = getScaleBandWidth(yScale) / 2;

    const xScaleInvert = getScaleInvert(xScale);
    const yScaleInvert = getScaleInvert(yScale);

    const getX = (x: number | null) => x === null ? null : (xScale(x) || 0) + xScaleOffset;
    const getX0 = (x0: number | null | undefined) => getX(x0 === undefined ? xDomain[0] : x0);
    const getY = (y: number | null) => y === null ? null : (yScale(y) || 0) + yScaleOffset;
    const getY0 = (y0: number | null | undefined) => getY(y0 === undefined ? yDomain[0] : y0);

    const getDomainX = (x: number) => xScaleInvert(x);
    const getDomainY = (y: number) => yScaleInvert(y);

    return {
      artboardId: refArtboardId.current,
      artboardBottom: artboardBottom,
      artboardHeight: artboardHeight,
      artboardLeft: artboardLeft,
      artboardRight: artboardRight,
      artboardTop: artboardTop,
      artboardWidth: artboardWidth,
      defArtboardClipMaskId: `${refArtboardId.current}-ArtboardClipMask`,
      getDomainX: getDomainX,
      getDomainY: getDomainY,
      getX: getX,
      getX0: getX0,
      getY: getY,
      getY0: getY0,
      height: height,
      ref: refContainer,
      transition: transition,
      width: width || size.width,
      xDomain: xDomain,
      xScale: xScale,
      yDomain: yDomain,
      yScale: yScale,
    };
  }, [
    height,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    size,
    transition,
    width,
    xDomain,
    xDomainPaddingInner,
    xDomainPaddingOuter,
    xDomainScale,
    yDomain,
    yDomainPaddingInner,
    yDomainPaddingOuter,
    yDomainScale,
  ]);

  const handlePointerMove: undefined | PointerEventHandler<HTMLDivElement> = onPointerMove && ((event) => {
    const { clientX, clientY, currentTarget } = event;
    const { left, top } = (currentTarget as Element).getBoundingClientRect();
    const relX = clientX - left;
    const relY = clientY - top;

    if (relX >= 0 && relX <= context.artboardRight &&
        relY >= 0 && relY <= context.artboardBottom) {
      onPointerMove(event, {
        x: context.getDomainX(relX),
        y: context.getDomainY(relY),
      });
    } else if (onPointerLeave) {
      onPointerLeave(event);
    }
  });

  return (
    <VizContext.Provider value={ context }>
      <div { ...rest }
          className={ classnames('Bitviz', className) }
          ref={ ref }
          style={ { ...styleViz, ...style, width } }>
        <div
            className="Bitviz__container"
            onPointerLeave={ onPointerLeave }
            onPointerMove={ handlePointerMove }
            ref={ refMerged }
            style={ { ...styleVizContainer,
              height: context.height,
              width: context.width,
            } }>
          { context.height !== 0 &&
            context.width !== 0 &&
            children }
        </div>
      </div>
    </VizContext.Provider>
  );
});

export default Viz;
